import { COLORS } from '../../constants/theme';
import { formatNumber } from '../../utils/format';
import { RANKING_STATS, RANKING_MOBILE_CARD_CAP, rankingStatValue } from '../../constants/rankings';
import type { RankingEntry, RankingStatKey, RankingsShellProps } from '../../types/rankings';

/**
 * SF-159 Component 3: mobile card list — exactly `entries.slice(0, RANKING_MOBILE_CARD_CAP)`, full
 * stop. No "scroll for more", no nested `overflow-y` container (Phase 3 Critical Issue 7) — plain
 * document flow, matching every other `Mobile{X}Shell`'s document-scroll model.
 *
 * Each card shows Rank / `{name} ({location})` (wraps rather than truncates) / the currently-
 * sorted stat's value + label only — matches the approved mockup's `.rc-stat` block exactly.
 */
export function MobileRankingsShell({
  entries,
  viewerEntry,
  currentTick,
  sortBy,
  onSortChange,
}: RankingsShellProps) {
  void currentTick; // province-only protection marker not surfaced on the compact mobile card

  const stat = RANKING_STATS.find((s) => s.key === sortBy);
  const visible = entries.slice(0, RANKING_MOBILE_CARD_CAP);
  const viewerInVisible = viewerEntry != null && visible.some((e) => e.id === viewerEntry.id);
  const showPinnedCard = viewerEntry != null && !viewerInVisible;

  function renderCard(entry: RankingEntry, pinned: boolean) {
    return (
      <div
        key={pinned ? `pinned-${entry.id}` : entry.id}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          backgroundColor: entry.isViewer ? 'rgba(240, 189, 60, 0.07)' : COLORS.cardAlt,
          border: `1px solid ${entry.isViewer ? COLORS.accentStrong : COLORS.border}`,
          borderStyle: pinned ? 'dashed' : 'solid',
          borderRadius: 7,
          padding: '10px 12px',
        }}
      >
        <div
          style={{
            fontFamily: 'JetBrains Mono, ui-monospace, monospace',
            fontWeight: 700,
            fontSize: 15,
            color: entry.position <= 3 && !pinned ? COLORS.gold : COLORS.text.secondary,
            width: 30,
            textAlign: 'center',
            flexShrink: 0,
          }}
        >
          {entry.position}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 13.5, color: COLORS.text.primary, overflowWrap: 'anywhere' }}>
            {entry.name} ({entry.location})
            {entry.isViewer && (
              <span style={{ color: COLORS.accentStrong, fontSize: 10, marginLeft: 6 }}>YOU</span>
            )}
          </div>
          {pinned && (
            <div style={{ fontSize: 10.5, color: COLORS.text.tertiary, marginTop: 2 }}>outside top 100</div>
          )}
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div
            style={{
              fontFamily: 'JetBrains Mono, ui-monospace, monospace',
              fontWeight: 700,
              fontSize: 14,
              color: stat?.key === 'networth' ? COLORS.gold : COLORS.text.primary,
            }}
          >
            {stat ? formatNumber(rankingStatValue(entry, stat.key)) : ''}
          </div>
          <div style={{ fontSize: 9.5, color: COLORS.text.tertiary, textTransform: 'uppercase', letterSpacing: 0.03 }}>
            {stat?.label}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <label
        style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, fontSize: 11, color: COLORS.text.tertiary, textTransform: 'uppercase', letterSpacing: 0.03 }}
      >
        Sort by
        <select
          value={sortBy}
          onChange={(e) => onSortChange(e.target.value as RankingStatKey)}
          style={{
            minHeight: 44,
            fontSize: 13,
            fontWeight: 600,
            backgroundColor: COLORS.cardAlt,
            color: COLORS.text.primary,
            border: `1px solid ${COLORS.border}`,
            borderRadius: 5,
            padding: '6px 8px',
          }}
        >
          {RANKING_STATS.map((s) => (
            <option key={s.key} value={s.key}>
              {s.label} (high–low)
            </option>
          ))}
        </select>
      </label>

      {visible.length === 0 && !showPinnedCard ? (
        <div style={{ padding: 24, textAlign: 'center', color: COLORS.text.muted }}>
          No provinces/kingdoms found in this scope
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {visible.map((entry) => renderCard(entry, false))}
          {showPinnedCard && renderCard(viewerEntry as RankingEntry, true)}
        </div>
      )}
    </div>
  );
}
